import React, { useEffect, useState, useCallback } from "react";
import {
  Card,
  CardBody,
  Button,
  Row,
  Col,
  Input,
  Label,
  Form,
} from "reactstrap";
import Request from "../../../helpers/request";
import * as url from "../../../helpers/url_helper";
import { toast } from "react-toastify";
import FlowBuilderCanvas, {
  type FlowBuilderData,
} from "./FlowBuilder/FlowBuilderCanvas";

// Match backend flowScheduleType enum
const FLOW_SCHEDULE_TYPE = {
  once: "once",
  daily: "daily",
  interval: "interval",
  monthly: "monthly",
  yearly: "yearly",
  weekly: "weekly",
} as const;
type FlowScheduleTypeKey = keyof typeof FLOW_SCHEDULE_TYPE;

interface AddFlowScreenProps {
  flowId?: string;
  initialData?: any;
  onExit: () => void;
  onSaveSuccess?: () => void;
}

interface FlowFormData {
  name: string;
  description: string;
  isActive: boolean;
  scheduleType: FlowScheduleTypeKey;
  // Time (1–12, minute, AM/PM)
  hour: number;
  minute: number;
  meridian: "AM" | "PM";
  // Once
  date: string;
  // Interval: backend requires exactly ONE of everyMinutes / everyHours / everyDays, value > 0
  intervalUnit: "everyMinutes" | "everyHours" | "everyDays";
  intervalValue: number;
  // Weekly: 0=Sun ... 6=Sat
  weekdays: number[];
  // Monthly: use either day (1–31) OR (weekOfMonth 1–5 + weekday 0–6), not both
  monthlyScheduleMode: "day" | "weekday";
  day: number;
  weekOfMonth: number;
  weekday: number;
  // Yearly
  month: number;
}

interface SegmentOption {
  _id: string;
  name: string;
}

interface TemplateOption {
  value: string;
  label: string;
}

function mapRowToFormData(row: any): FlowFormData {
  const schedule = row?.schedule ?? {};
  const type = schedule.type ?? "interval";
  const dateStr = schedule.date
    ? new Date(schedule.date).toISOString().slice(0, 10)
    : "";
  const intervalVal =
    schedule.everyMinutes ?? schedule.everyHours ?? schedule.everyDays ?? 5;
  const intervalUnit = schedule.everyMinutes
    ? "everyMinutes"
    : schedule.everyHours
    ? "everyHours"
    : "everyDays";
  return {
    name: row?.name ?? "",
    description: row?.description ?? "",
    isActive: row?.isActive ?? true,
    scheduleType: type in FLOW_SCHEDULE_TYPE ? type : "once",
    hour: schedule.hour ?? 9,
    minute: schedule.minute ?? 0,
    meridian: schedule.meridian === "PM" ? "PM" : "AM",
    date: dateStr,
    intervalUnit,
    intervalValue: typeof intervalVal === "number" ? intervalVal : 5,
    weekdays: Array.isArray(schedule.weekdays) ? schedule.weekdays : [],
    monthlyScheduleMode:
      schedule.day != null && schedule.day > 0 ? "day" : "weekday",
    day: schedule.day ?? 1,
    weekOfMonth: schedule.weekOfMonth ?? 1,
    weekday: schedule.weekday ?? 0,
    month: schedule.month ?? 1,
  };
}

function mapRowToFlowBuilderData(row: any): FlowBuilderData | null {
  const segmentId = row?.segmentId ?? row?.segment?._id ?? "";
  if (!segmentId) return null;
  const seg = row?.segment;
  const segmentName = seg?.name ?? row?.segmentName ?? "";
  const notif = row?.notification ?? {};
  const baseSegmentFields = {
    segmentId,
    segmentName,
    segmentDescription: seg?.description,
    segmentTotalUsersCount: seg?.totalUsersCount,
    segmentPercentage: seg?.percentage,
    segmentUpdateCycle: seg?.updateCycle,
    segmentLastUpdatedAt: seg?.lastUpdatedAt,
    segmentIsActive: seg?.isActive,
    segmentIsLiveSegmentation: seg?.isLiveSegmentation,
  };
  if (notif.type === "template") {
    return {
      ...baseSegmentFields,
      notificationType: "template",
      templateId: notif.templateId ?? "",
    };
  }
  return {
    ...baseSegmentFields,
    notificationType: "custom",
    title: notif.title ?? "",
    body: notif.body ?? "",
    image: notif.image,
    link: notif.link,
    buttons: notif.buttons,
  };
}

const AddFlowScreen: React.FC<AddFlowScreenProps> = ({
  flowId,
  initialData,
  onExit,
  onSaveSuccess,
}) => {
  const [loading, setLoading] = useState(false);
  const [segments, setSegments] = useState<SegmentOption[]>([]);
  const [templates, setTemplates] = useState<TemplateOption[]>([]);
  const [flowBuilderData, setFlowBuilderData] =
    useState<FlowBuilderData | null>(null);
  const [formData, setFormData] = useState<FlowFormData>({
    name: "",
    description: "",
    isActive: true,
    scheduleType: "once",
    hour: 19,
    minute: 0,
    meridian: "AM",
    date: "",
    intervalUnit: "everyMinutes",
    intervalValue: 5,
    weekdays: [],
    monthlyScheduleMode: "day",
    day: 1,
    weekOfMonth: 1,
    weekday: 0,
    month: 1,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (initialData) {
      setFormData(mapRowToFormData(initialData));
      const builderData = mapRowToFlowBuilderData(initialData);
      if (builderData) setFlowBuilderData(builderData);
    }
  }, [initialData]);

  // Segments are fetched by SegmentNode when the user opens the segment picker offcanvas (same API, server-side search + pagination). No pre-fetch here to avoid duplicate API calls.

  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const response = await Request.post(
          `${url.BASE_URL}${url.GET_NOTIFICATION_TEMPLATE}`,
          { page: 1, limit: 100, isForERP: false, search: "" },
        );
        const list = response?.data?.data?.templates || [];
        const normalized = list.map((tpl: any) => {
          const rawLabel = tpl.name || tpl.headings || tpl.id || "";
          const templateId = tpl.id?.toString() || tpl._id?.toString() || "";
          return {
            value: templateId,
            label: typeof rawLabel === "string" ? rawLabel : String(rawLabel),
          };
        });
        setTemplates(normalized);
      } catch (err: any) {
        console.error("Error fetching templates:", err);
        setTemplates([]);
      }
    };
    fetchTemplates();
  }, []);

  const handleFlowDataChange = useCallback((data: FlowBuilderData | null) => {
    setFlowBuilderData(data);
  }, []);

  const handleFieldChange = (field: keyof FlowFormData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!formData.name.trim()) newErrors.name = "Flow name is required";
    if (!formData.description.trim())
      newErrors.description = "Description is required";
    if (!flowBuilderData?.segmentId)
      newErrors.flowBuilder =
        "Select a segment and connect it to Custom Notification or Template";
    if (
      flowBuilderData?.notificationType === "custom" &&
      !flowBuilderData.title?.trim()
    )
      newErrors.flowBuilder =
        "Enter notification title when using Custom Notification";
    if (
      flowBuilderData?.notificationType === "template" &&
      !flowBuilderData.templateId
    )
      newErrors.flowBuilder = "Select a template when using Template";

    if (formData.scheduleType === "once") {
      if (!formData.date.trim())
        newErrors.schedule = "Date is required for Once schedule";
      else {
        const runDate = new Date(formData.date);
        if (isNaN(runDate.getTime()))
          newErrors.schedule = "Invalid date format";
        else if (runDate < new Date())
          newErrors.schedule = "Schedule date must be in the future";
      }
    }
    if (formData.scheduleType === "interval") {
      const v = formData.intervalValue;
      if (v == null || v < 1)
        newErrors.schedule = "Interval value must be greater than 0";
      else if (
        formData.intervalUnit === "everyMinutes" &&
        (v < 1 || v > 1440)
      )
        newErrors.intervalValue = "Minutes must be between 1 and 1440";
    }
    if (formData.scheduleType === "weekly" && formData.weekdays.length === 0)
      newErrors.schedule = "Select at least one weekday for Weekly schedule";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate() || !flowBuilderData) return;

    setLoading(true);
    try {
      // Backend flow model: notification { type: "template" | "manual", templateId?, title?, body?, image?, link?, buttons? }
      const notification =
        flowBuilderData.notificationType === "custom"
          ? (() => {
              const base: {
                type: "manual";
                title: string;
                body: string;
                image?: string;
                link?: string;
                buttons?: { id: string; text: string; url: string }[];
              } = {
                type: "manual",
                title: flowBuilderData.title?.trim() ?? "",
                body: flowBuilderData.body?.trim() ?? "",
              };
              const image = flowBuilderData.image?.trim();
              const link = flowBuilderData.link?.trim();
              const buttons = flowBuilderData.buttons?.filter(
                (b) => b?.text?.trim() && b?.url?.trim(),
              );
              if (image) base.image = image;
              if (link) base.link = link;
              if (buttons?.length)
                base.buttons = buttons.map((b, i) => ({
                  id: b.id?.trim() || `btn${i + 1}`,
                  text: b.text.trim(),
                  url: b.url.trim(),
                }));
              return base;
            })()
          : {
              type: "template" as const,
              templateId: flowBuilderData.templateId ?? "",
            };

      // Backend flow model: schedule { type, hour?, minute?, meridian?, date?, everyMinutes?, everyHours?, everyDays?, weekdays?, day?, weekOfMonth?, weekday?, month? }
      const buildSchedule = () => {
        const base = {
          minute: formData.minute,
        };
        const type = FLOW_SCHEDULE_TYPE[formData.scheduleType];
        switch (formData.scheduleType) {
          case "once":
            return {
              ...base,
              type,
              hour: formData.hour,
              meridian: formData.meridian,
              date: formData.date
                ? new Date(formData.date).toISOString()
                : undefined,
            };
          case "daily":
            return {
              ...base,
              type,
              hour: formData.hour,
              meridian: formData.meridian,
            };
          case "interval":
            return {
              ...base,
              type,
              [formData.intervalUnit]: formData.intervalValue,
            };
          case "weekly":
            return {
              ...base,
              type,
              hour: formData.hour,
              meridian: formData.meridian,
              weekdays: formData.weekdays,
            };
          case "monthly":
            return formData.monthlyScheduleMode === "day"
              ? {
                  ...base,
                  type,
                  hour: formData.hour,
                  meridian: formData.meridian,
                  day: formData.day,
                }
              : {
                  ...base,
                  type,
                  hour: formData.hour,
                  meridian: formData.meridian,
                  weekOfMonth: formData.weekOfMonth,
                  weekday: formData.weekday,
                };
          case "yearly":
            return {
              ...base,
              type,
              hour: formData.hour,
              meridian: formData.meridian,
              month: formData.month,
              day: formData.day,
            };
          default:
            return { ...base, type: "once" as const };
        }
      };

      const payload = {
        name: formData.name.trim(),
        description: formData.description.trim() || "",
        segmentId: flowBuilderData.segmentId,
        schedule: buildSchedule(),
        notification,
        isActive: formData.isActive,
      };

      if (flowId) {
        await Request.put(`${url.BASE_URL}${url.UPDATE_FLOW}`, {
          flowId,
          ...payload,
        });
        toast.success("Flow updated successfully");
      } else {
        await Request.post(`${url.BASE_URL}${url.ADD_FLOW}`, payload);
        toast.success("Flow created successfully");
      }

      setFormData({
        name: "",
        description: "",
    isActive: true,
    scheduleType: "interval",
        hour: 9,
        minute: 0,
        meridian: "AM",
        date: "",
        intervalUnit: "everyMinutes",
        intervalValue: 5,
        weekdays: [],
        monthlyScheduleMode: "day",
        day: 1,
        weekOfMonth: 1,
        weekday: 0,
        month: 1,
      });
      setFlowBuilderData(null);
      onSaveSuccess?.();
      onExit();
    } catch (error: any) {
      console.error("Error creating flow:", error);
      if (error?.response?.status === 401) {
        localStorage.removeItem("userInfo");
        localStorage.removeItem("selectedPremiseID");
        localStorage.removeItem("selectedUserID");
        localStorage.removeItem("selectedUserEmail");
        localStorage.removeItem("Authorization");
        localStorage.removeItem("permissions");
        window.location.reload();
      } else {
        toast.error(
          error?.response?.data?.message ||
            error?.response?.data?.msg ||
            "Failed to create flow",
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const formLabelClass = "form-label fw-semibold text-secondary mb-1";

  return (
    <Row>
      <Col>
        <Card
          style={{
            marginTop: 100,
            marginLeft: 30,
            marginRight: 30,
            marginBottom: 0,
          }}
        >
          <CardBody>
            {/* Top bar: Exit (left) | Save (right) */}
            <div
              className="d-flex justify-content-between align-items-center mb-3"
              style={{ flexWrap: "wrap", gap: 8 }}
            >
              <h5 className="m-0 fw-semibold text-dark">
                {flowId ? "Edit Flow" : "Add Flow"}
              </h5>
              <div className="d-flex w-full" style={{ gap: 8 }}>
                <Button
                  className="btn btn-dark"
                  size="sm"
                  disabled={loading}
                  onClick={onExit}
                >
                  Exit
                </Button>
                <Button
                  color="btn btn-dark"
                  size="sm"
                  disabled={loading}
                  onClick={handleSave}
                >
                  {loading && (
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      aria-hidden="true"
                    />
                  )}
                  {flowId ? "Update  Flow" : "Save Flow"}
                </Button>
              </div>
            </div>

            {/* Top section: all input fields */}
            <div
              className="px-3 pt-3 mb-3 rounded"
              style={{ border: "1px solid #e9ecef" }}
            >
              <Form>
                <Row>
                  <Col sm={6} lg={3} className="mb-3">
                    <Label className="form-label">
                      Flow Name <span className="text-danger">*</span>
                    </Label>
                    <Input
                      type="text"
                      placeholder="Enter flow name"
                      value={formData.name}
                      onChange={(e) =>
                        handleFieldChange("name", e.target.value)
                      }
                      invalid={!!errors.name}
                    />
                    {errors.name && (
                      <div
                        className="text-danger mt-1"
                        style={{ fontSize: "0.875rem" }}
                      >
                        {errors.name}
                      </div>
                    )}
                  </Col>
                  <Col sm={6} lg={3} className="mb-3">
                    <Label className="form-label">
                      Description <span className="text-danger">*</span>
                    </Label>
                    <Input
                      type="textarea"
                      rows={1}
                      placeholder="Enter description"
                      value={formData.description}
                      onChange={(e) =>
                        handleFieldChange("description", e.target.value)
                      }
                      invalid={!!errors.description}
                    />
                    {errors.description && (
                      <div
                        className="text-danger mt-1"
                        style={{ fontSize: "0.875rem" }}
                      >
                        {errors.description}
                      </div>
                    )}
                  </Col>
                  <Col sm={6} lg={3} className="mb-3 d-flex align-items-center">
                    <div className="form-check form-switch">
                      <Input
                        type="checkbox"
                        className="form-check-input"
                        id="flowIsActive"
                        checked={formData.isActive}
                        onChange={(e) =>
                          handleFieldChange("isActive", e.target.checked)
                        }
                      />
                      <Label
                        className="form-check-label"
                        htmlFor="flowIsActive"
                      >
                        Active
                      </Label>
                    </div>
                  </Col>
                </Row>

                <Row>
                      <Col sm={6} lg={3} className="mb-3">
                        <Label className="form-label">Schedule type</Label>
                        <Input
                          type="select"
                          value={formData.scheduleType}
                          onChange={(e) =>
                            handleFieldChange(
                              "scheduleType",
                              e.target.value as FlowScheduleTypeKey,
                            )
                          }
                        >
                          {(
                            Object.keys(
                              FLOW_SCHEDULE_TYPE,
                            ) as FlowScheduleTypeKey[]
                          ).map((key) => (
                            <option key={key} value={key}>
                              {key.charAt(0).toUpperCase() + key.slice(1)}
                            </option>
                          ))}
                        </Input>
                      </Col>

                      {(formData.scheduleType === "once" ||
                        formData.scheduleType === "daily" ||
                        formData.scheduleType === "weekly" ||
                        formData.scheduleType === "monthly" ||
                        formData.scheduleType === "yearly") && (
                        <Col sm={6} lg={3} className="mb-3">
                          <Label className="form-label">Time</Label>
                          <div className="d-flex gap-2 align-items-center flex-wrap">
                            <Input
                              type="select"
                              value={formData.hour}
                              onChange={(e) =>
                                handleFieldChange(
                                  "hour",
                                  parseInt(e.target.value, 10),
                                )
                              }
                              style={{ width: 70 }}
                            >
                              {Array.from({ length: 12 }, (_, i) => i + 1).map(
                                (h) => (
                                  <option key={h} value={h}>
                                    {h}
                                  </option>
                                ),
                              )}
                            </Input>
                            <span>:</span>
                            <Input
                              type="select"
                              value={formData.minute}
                              onChange={(e) =>
                                handleFieldChange(
                                  "minute",
                                  parseInt(e.target.value, 10),
                                )
                              }
                              style={{ width: 80 }}
                            >
                              {[0, 15, 30, 45].map((m) => (
                                <option key={m} value={m}>
                                  {m.toString().padStart(2, "0")}
                                </option>
                              ))}
                            </Input>
                            <Input
                              type="select"
                              value={formData.meridian}
                              onChange={(e) =>
                                handleFieldChange(
                                  "meridian",
                                  e.target.value as "AM" | "PM",
                                )
                              }
                              style={{ width: 70 }}
                            >
                              <option value="AM">AM</option>
                              <option value="PM">PM</option>
                            </Input>
                          </div>
                        </Col>
                      )}

                      {formData.scheduleType === "once" && (
                        <Col sm={6} lg={3} className="mb-3">
                          <Label className="form-label">Date</Label>
                          <Input
                            type="date"
                            value={formData.date}
                            onChange={(e) =>
                              handleFieldChange("date", e.target.value)
                            }
                            invalid={!!errors.schedule}
                          />
                          {errors.schedule && (
                            <div className="text-danger small mt-1">
                              {errors.schedule}
                            </div>
                          )}
                        </Col>
                      )}

                      {formData.scheduleType === "interval" && (
                        <>
                          <Col sm={6} lg={3} className="mb-3">
                            <Label className="form-label">Repeat every</Label>
                            <Input
                              type="select"
                              value={formData.intervalUnit}
                              onChange={(e) =>
                                handleFieldChange(
                                  "intervalUnit",
                                  e.target.value as
                                    | "everyMinutes"
                                    | "everyHours"
                                    | "everyDays",
                                )
                              }
                            >
                              <option value="everyMinutes">Minutes</option>
                              <option value="everyHours">Hours</option>
                              <option value="everyDays">Days</option>
                            </Input>
                          </Col>
                          <Col sm={6} lg={3} className="mb-3">
                            <Label className="form-label">
                              Value (must be &gt; 0)
                            </Label>
                            <Input
                              type="number"
                              min={1}
                              max={
                                formData.intervalUnit === "everyMinutes"
                                  ? 1440
                                  : undefined
                              }
                              placeholder={
                                formData.intervalUnit === "everyMinutes"
                                  ? "1–1440"
                                  : "1"
                              }
                              value={formData.intervalValue ?? ""}
                              onChange={(e) =>
                                handleFieldChange(
                                  "intervalValue",
                                  parseInt(e.target.value, 10) || 0,
                                )
                              }
                              invalid={!!errors.intervalValue}
                            />
                            {errors.intervalValue && (
                              <div className="text-danger small mt-1">
                                {errors.intervalValue}
                              </div>
                            )}
                            {errors.schedule &&
                              formData.scheduleType === "interval" && (
                                <div className="text-danger small mt-1">
                                  {errors.schedule}
                                </div>
                              )}
                          </Col>
                        </>
                      )}

                      {formData.scheduleType === "weekly" && (
                        <Col sm={12} lg={6} className="mb-3">
                          <Label className="form-label">Weekdays</Label>
                          <div className="d-flex gap-2 flex-wrap">
                            {[
                              { v: 0, l: "Sun" },
                              { v: 1, l: "Mon" },
                              { v: 2, l: "Tue" },
                              { v: 3, l: "Wed" },
                              { v: 4, l: "Thu" },
                              { v: 5, l: "Fri" },
                              { v: 6, l: "Sat" },
                            ].map(({ v, l }) => (
                              <div
                                key={v}
                                className="form-check form-check-inline"
                              >
                                <Input
                                  type="checkbox"
                                  id={`wd-${v}`}
                                  checked={formData.weekdays.includes(v)}
                                  onChange={(e) => {
                                    const next = e.target.checked
                                      ? [...formData.weekdays, v].sort(
                                          (a, b) => a - b,
                                        )
                                      : formData.weekdays.filter(
                                          (d) => d !== v,
                                        );
                                    handleFieldChange("weekdays", next);
                                  }}
                                />
                                <Label
                                  className="form-check-label"
                                  htmlFor={`wd-${v}`}
                                >
                                  {l}
                                </Label>
                              </div>
                            ))}
                          </div>
                          {errors.schedule &&
                            formData.scheduleType === "weekly" && (
                              <div className="text-danger small mt-1">
                                {errors.schedule}
                              </div>
                            )}
                        </Col>
                      )}

                      {formData.scheduleType === "monthly" && (
                        <>
                          <Col sm={6} lg={3} className="mb-3">
                            <Label className="form-label">Monthly on</Label>
                            <div className="d-flex flex-wrap gap-2">
                              <div className="form-check">
                                <Input
                                  type="radio"
                                  className="form-check-input"
                                  name="monthlyMode"
                                  id="monthlyModeDay"
                                  checked={
                                    formData.monthlyScheduleMode === "day"
                                  }
                                  onChange={() =>
                                    handleFieldChange(
                                      "monthlyScheduleMode",
                                      "day",
                                    )
                                  }
                                />
                                <Label
                                  className="form-check-label"
                                  htmlFor="monthlyModeDay"
                                >
                                  Day of month
                                </Label>
                              </div>
                              <div className="form-check">
                                <Input
                                  type="radio"
                                  className="form-check-input"
                                  name="monthlyMode"
                                  id="monthlyModeWeekday"
                                  checked={
                                    formData.monthlyScheduleMode === "weekday"
                                  }
                                  onChange={() =>
                                    handleFieldChange(
                                      "monthlyScheduleMode",
                                      "weekday",
                                    )
                                  }
                                />
                                <Label
                                  className="form-check-label"
                                  htmlFor="monthlyModeWeekday"
                                >
                                  Weekday (e.g. 2nd Tue)
                                </Label>
                              </div>
                            </div>
                          </Col>
                          {formData.monthlyScheduleMode === "day" && (
                            <Col sm={6} lg={3} className="mb-3">
                              <Label className="form-label">Day (1–31)</Label>
                              <Input
                                type="number"
                                min={1}
                                max={31}
                                value={formData.day || 1}
                                onChange={(e) =>
                                  handleFieldChange(
                                    "day",
                                    parseInt(e.target.value, 10) || 1,
                                  )
                                }
                                className="border-secondary border-opacity-25"
                              />
                            </Col>
                          )}
                          {formData.monthlyScheduleMode === "weekday" && (
                            <>
                              <Col sm={6} lg={3} className="mb-3">
                                <Label className="form-label">
                                  Week of month (1–5)
                                </Label>
                                <Input
                                  type="number"
                                  min={1}
                                  max={5}
                                  value={formData.weekOfMonth || 1}
                                  onChange={(e) =>
                                    handleFieldChange(
                                      "weekOfMonth",
                                      parseInt(e.target.value, 10) || 1,
                                    )
                                  }
                                  className="border-secondary border-opacity-25"
                                />
                              </Col>
                              <Col sm={6} lg={3} className="mb-3">
                                <Label className="form-label">
                                  Weekday (0=Sun–6=Sat)
                                </Label>
                                <Input
                                  type="number"
                                  min={0}
                                  max={6}
                                  value={formData.weekday ?? 0}
                                  onChange={(e) =>
                                    handleFieldChange(
                                      "weekday",
                                      parseInt(e.target.value, 10) || 0,
                                    )
                                  }
                                  className="border-secondary border-opacity-25"
                                />
                              </Col>
                            </>
                          )}
                        </>
                      )}

                      {formData.scheduleType === "yearly" && (
                        <>
                          <Col sm={6} lg={3} className="mb-3">
                            <Label className="form-label">Month</Label>
                            <Input
                              type="select"
                              value={formData.month || 1}
                              onChange={(e) =>
                                handleFieldChange(
                                  "month",
                                  parseInt(e.target.value, 10) || 1,
                                )
                              }
                            >
                              {[
                                "Jan",
                                "Feb",
                                "Mar",
                                "Apr",
                                "May",
                                "Jun",
                                "Jul",
                                "Aug",
                                "Sep",
                                "Oct",
                                "Nov",
                                "Dec",
                              ].map((label, i) => (
                                <option key={i} value={i + 1}>
                                  {label}
                                </option>
                              ))}
                            </Input>
                          </Col>
                          <Col sm={6} lg={3} className="mb-3">
                            <Label className="form-label">Day (1–31)</Label>
                            <Input
                              type="number"
                              min={1}
                              max={31}
                              value={formData.day || 1}
                              onChange={(e) =>
                                handleFieldChange(
                                  "day",
                                  parseInt(e.target.value, 10) || 1,
                                )
                              }
                            />
                          </Col>
                        </>
                      )}
                    </Row>
              </Form>
            </div>

            {/* Segment & Notification + Flow board */}
            <div className="mb-0">
              <Label className="form-label fw-bold">
                Segment &amp; Notification{" "}
                <span className="text-danger">*</span>
              </Label>
              <p className="text-muted small mb-2">
                Select a segment, then connect it (drag from the right handle)
                to either Custom Notification or Template.
              </p>
              {errors.flowBuilder && (
                <div
                  className="text-danger mt-1"
                  style={{ fontSize: "0.875rem" }}
                >
                  {errors.flowBuilder}
                </div>
              )}
              <FlowBuilderCanvas
                key={flowId ?? "new"}
                segments={segments}
                templates={templates}
                onFlowDataChange={handleFlowDataChange}
                initialFlowData={
                  initialData ? mapRowToFlowBuilderData(initialData) : null
                }
              />
            </div>
          </CardBody>
        </Card>
      </Col>
    </Row>
  );
};

export default AddFlowScreen;
